// SPEC-029 M3: AI participants are talking heads each viewer renders, never video. The head is the
// landing page's (alpha.pioneers.dev/head/), driven over postMessage.
// ponytail: likeness, voices and the face-curve mapping are copied from ~/pioneer-landing app.js so an agent
// looks and sounds the same on both pages. Change them there first, then here; share a module if they drift.
import { request, type Line, type Session } from "./api";

export const HEAD_URL = import.meta.env.VITE_PIONEER_HEAD_URL || "https://alpha.pioneers.dev/head/";
const VOICES: Record<string, string> = {
	"Male · friendly": "v_96fcee3bf51be8472e41bdc0",
	"Male · bright": "v_d95401f2c5811c1af4e7c8bb",
	"Male · narrator": "v_c87af70991fc80411604476c",
	"Female · warm": "v_051f2fee9c8c16b7dc674d62",
};
export const HOST = "pioneer";
// The face model's 52 named channels, grouped onto the head's shapes (head-bridge.ts in the landing repo).
const FACE_MAP: Record<string, string[][]> = {
	open: [["jawOpen"]], funnel: [["mouthFunnel"]], pucker: [["mouthPucker"]],
	grin: [["mouthSmileLeft", "mouthSmileRight"]], stretch: [["mouthStretchLeft", "mouthStretchRight"]],
	frown: [["mouthFrownLeft", "mouthFrownRight"]],
	close: [["mouthClose"], ["mouthPressLeft", "mouthPressRight"]],
	upper: [["mouthUpperUpLeft", "mouthUpperUpRight"], ["noseSneerLeft", "noseSneerRight"]],
	brow: [["browInnerUp"], ["browOuterUpLeft", "browOuterUpRight"]], squint: [["eyeSquintLeft", "eyeSquintRight"]],
};
const GAIN_OF: Record<string, string> = { open: "jaw", funnel: "round", pucker: "round", close: "close", brow: "upper", squint: "upper" };
const FACE_GAIN: Record<string, number> = { jaw: 1.5, lips: 1.5, round: 2.5, close: 2, upper: 1.2 };
const PARTS_LIPS = ["open", "funnel", "grin", "stretch", "upper"];

// What the head model can make, reported by the first head that loads.
export let samplers: { genders: string[]; ethnicities: string[] } = { genders: [], ethnicities: [] };
export function setSamplers(data: { genders?: string[]; ethnicities?: string[] }) {
	if (data.genders?.length) samplers = { genders: data.genders, ethnicities: data.ethnicities || [] };
}
const hash = (s: string) => [...s].reduce((h, c) => Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0, 2166136261);
const pick = (list: string[], n: number) => (list.length ? list[n % list.length] : "blend");
export function agentLook(slug: string) {
	const h = hash(slug);
	return { gender: pick(samplers.genders, h), ethnicity: pick(samplers.ethnicities, h >>> 8), seed: h, sigma: 0.9 };
}
// Pioneer has no face of its own: a new one every two seconds.
export function hostFace() {
	const any = (list: string[]) => (list.length ? list[Math.floor(Math.random() * list.length)] : "blend");
	return { gender: any(samplers.genders), ethnicity: any(samplers.ethnicities), seed: Math.floor(Math.random() * 2 ** 32), sigma: 0.9 };
}
export const agentColor = (slug: string) => `hsl(${hash(slug) % 360} 62% 62%)`;
export function agentVoice(slug: string) {
	if (slug === HOST) return VOICES["Male · friendly"];
	const female = agentLook(slug).gender === "female";
	const ids = Object.entries(VOICES).filter(([name]) => /^Female/.test(name) === female).map(([, id]) => id);
	return ids[hash(slug) % ids.length];
}

interface Face { fps: number; channels: string[]; weights: number[][] }
// The head's shape weights at a playback time, blended between the two nearest curve frames.
function faceShapes(face: Face) {
	const index = Object.fromEntries(face.channels.map((name, i) => [name, i]));
	const last = face.weights.length - 1;
	const shapes = Object.entries(FACE_MAP).map(([shape, groups]) => [shape, groups.map((g) => g.map((n) => index[n]).filter((i) => i !== undefined))] as const);
	return (t: number) => {
		const at = Math.min(last, Math.max(0, t * face.fps)), lo = Math.floor(at), k = at - lo;
		const a = face.weights[lo], b = face.weights[Math.min(last, lo + 1)];
		const w: Record<string, number> = {};
		for (const [shape, groups] of shapes) {
			let value = 0;
			for (const g of groups) if (g.length) value = Math.max(value, g.reduce((sum, i) => sum + a[i] + (b[i] - a[i]) * k, 0) / g.length);
			w[shape] = value * FACE_GAIN[GAIN_OF[shape] || "lips"];
		}
		// Rounded lips cannot also be pulled wide, and closed lips part nothing.
		const rounded = Math.min(1, w.pucker + w.funnel), sealed = Math.min(1, w.close);
		w.grin *= 1 - rounded;
		w.stretch *= 1 - rounded;
		for (const shape of PARTS_LIPS) w[shape] *= 1 - sealed;
		return w;
	};
}

// Plays the room's lines in order, each on its speaker's head. Lines already in the room when this viewer
// arrived are history and stay silent.
export class Stage {
	readonly heads = new Map<string, Window>();
	onSpeaking = (slug: string | null) => {};
	private seen = new Set<string>();
	private done = new Set<string>();
	private waiters = new Map<string, () => void>();
	private queue = Promise.resolve();
	private primed = false;
	readonly audio = new AudioContext();
	constructor(private meeting: string, private session: Session) {}
	take(lines: Line[]) {
		for (const line of lines) {
			if (this.seen.has(line.id)) continue;
			this.seen.add(line.id);
			if (!this.primed) {
				this.finish(line.id);
				continue;
			}
			this.queue = this.queue
				.then(() => this.play(line))
				.catch((e) => console.warn("[meet] line", e instanceof Error ? e.message : e))
				.then(() => this.finish(line.id));
		}
		this.primed = true;
	}
	played(id: string) {
		return this.done.has(id) ? Promise.resolve() : new Promise<void>((resolve) => this.waiters.set(id, resolve));
	}
	// Resolves once every listed head has loaded, or after the timeout.
	async ready(slugs: string[], timeout: number) {
		const end = Date.now() + timeout;
		while (Date.now() < end && !slugs.every((s) => this.heads.has(s))) await new Promise((r) => setTimeout(r, 250));
	}
	close() {
		void this.audio.close();
	}
	private finish(id: string) {
		this.done.add(id);
		this.waiters.get(id)?.();
		this.waiters.delete(id);
	}
	private async play(line: Line) {
		const head = this.heads.get(line.speaker);
		const post = (message: Record<string, unknown>) => head?.postMessage(message, "*");
		post({ type: "pioneer-head-mood", mood: line.mood, weight: 0.9 });
		post({ type: "pioneer-head-state", state: "speaking", inputActive: false });
		this.onSpeaking(line.speaker);
		try {
			if (!line.clip) {
				// No voice for this line: hold the floor about as long as reading it takes.
				await new Promise((r) => setTimeout(r, Math.min(8000, 1000 + line.text.length * 50)));
				return;
			}
			const clip = await request<{ audio: { base64: string }; face?: Face }>(`/api/v1/meetings/${this.meeting}/clips/${line.clip}`, this.session, undefined, { timeout: 30_000 });
			const wav = Uint8Array.from(atob(clip.audio.base64), (c) => c.charCodeAt(0)).buffer;
			await this.audio.resume();
			const decoded = await this.audio.decodeAudioData(wav);
			const src = this.audio.createBufferSource();
			src.buffer = decoded;
			const analyser = this.audio.createAnalyser();
			analyser.fftSize = 1024;
			src.connect(analyser);
			analyser.connect(this.audio.destination);
			const curves = clip.face?.weights?.length ? faceShapes(clip.face) : null;
			const freq = new Uint8Array(analyser.frequencyBinCount);
			await new Promise<void>((resolve) => {
				let live = true;
				src.onended = () => {
					live = false;
					resolve();
				};
				const t0 = this.audio.currentTime;
				src.start();
				const tick = () => {
					if (!live) return;
					if (curves) post({ type: "pioneer-head-face", weights: curves(this.audio.currentTime - t0) });
					else {
						// Without curves the mouth follows the sound's low band.
						analyser.getByteFrequencyData(freq);
						const low = freq.slice(2, 10).reduce((s, v) => s + v, 0) / 8 / 255;
						post({ type: "pioneer-head-mouth", value: { jaw: Math.min(0.85, Math.pow(low * 1.6, 1.7)), round: 0, wide: 0 } });
					}
					requestAnimationFrame(tick);
				};
				tick();
			});
		} finally {
			post({ type: "pioneer-head-face", weights: null });
			post({ type: "pioneer-head-mouth", value: { jaw: 0, round: 0, wide: 0 } });
			post({ type: "pioneer-head-state", state: "idle", inputActive: false });
			post({ type: "pioneer-head-mood", mood: "neutral", weight: 0.5 });
			this.onSpeaking(null);
		}
	}
}
